import { useRef,  useState , memo , useCallback} from 'react';
import Map from '../components/map/Map.js';
import Grid from '@mui/material/Grid';
import {useFetchHistory} from '../hooks/useFetchHistory';
import {useFetchRouting} from '../hooks/useFetchRouting';
import {Marker ,  Tooltip , Polyline} from 'react-leaflet';
import Header from "../components/Header";
import MapCardHistory from '../components/map/MapCardHistory.js'
import MapListHistory from '../components/map/MapListHistory.js'
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {useSharedLang} from '../hooks/useSharedLang.js';
import {LANGS , getInsta360Url , geoPositionOptions} from "../components/constants.js";
import Loading from "../components/Loading.js";
import {useGeolocated} from '../hooks/useGeolocated';
import {useDikstra} from '../hooks/useDikstra';
import {useNavigate} from "react-router-dom";
import {getLink , getIcon, getTooltipOptions} from "../utils/UrlUtil.js";

const History = memo(() => {
    const mapRef = useRef(null);

    const {historyList, isLoading} = useFetchHistory();
    const {routingList, isRouteLoading} = useFetchRouting();
    const [isList , setIsList] = useState(false); //一覧表示状態
    const [inRouteGuid, setInRouteGuid] = useState(false);
    const [selectedHistory , setSelectedHistory] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const selectedMarkerKey = useRef(null);
    const defaultSelectedHistory = useRef(null);
    const isUseDefaultSelectedHistory = useRef(true);
    const activeHistoryList = useRef(null);

    const { id } = useParams();
    const { t } = useTranslation();
    const { sharedLang } = useSharedLang();
    const navigate = useNavigate();
    
    /**
     * リアルタイム座標チェック
     * 
     * @param {*} pos 
     */
    const setCurrentPosition = (pos) => {
        //現在地をマップに表示
        if(mapRef.current != null){
            mapRef.current.updateMarker(pos.coords.latitude, pos.coords.longitude);
            lastCoords.current =  pos;
        }
    };

    //--- 現在地 ---
    //useGeolocated の coordsは使わない。使うと座標が更新されるたびに画面が再描写してしまう。
    const { isGeolocationAvailable, isGeolocationEnabled  } = useGeolocated({
        ...geoPositionOptions , 
        onSuccess : (pos) => setCurrentPosition(pos),
    });
    const lastCoords = useRef(null);

    const { routing, getNearNode } = useDikstra({routingData : routingList})

    const getMarkerKey = useCallback((history , coordinate) => {
        return history.id + '-' + coordinate[0] + '-' + coordinate[1];
    },[]);

    const parseMarkerKey = useCallback((markerKey) => {
        const [id, latitude, longitude] = markerKey.split('-');
        return {id:id , longitude:longitude, latitude:latitude}
    },[]);

    const toggleList = useCallback((flg) => {
        setIsList(flg);
    },[]);

    const toggleRouteGuid = (flg) => {
        setInRouteGuid(flg);
    };

    const isCanGPS = ()=>{
        return isGeolocationAvailable && isGeolocationEnabled && lastCoords.current != null;
    }

    const clickMarker = (history, coordinate) => {
        selectedMarkerKey.current = getMarkerKey(history, coordinate);
        setSelectedHistory(history);
    };

    const resetMarker = useCallback(() => {
        selectedMarkerKey.current = null;
        isUseDefaultSelectedHistory.current = false;
        setSelectedHistory(null);
        setInRouteGuid(false);
    },[]);
    
    const selectHistory = useCallback((history) => {
        selectedMarkerKey.current = null;
        setSelectedHistory(history);
        setIsList(false);
    },[]);

    const isActiveMarker = (history) => { 
        let currentElement = getActiveElement();    
        return !currentElement || currentElement.id === history.id;
    };
    
    const modalOpen = useCallback(() => {
        // setIsOpen(true);
        navigate(
            '/modal360', 
            { state: { link360: getInsta360Url(selectedHistory) ,returnUrl: getLink(selectedHistory)}
        });

    },[selectedHistory]);

    const getActiveElement = useCallback(() => {
        let ret = selectedHistory;
        if(selectedHistory == null && isUseDefaultSelectedHistory.current){
            ret = defaultSelectedHistory.current;
        }
        return ret;
    },[selectedHistory]);

    const getPolyline = useCallback(() => {
        let selectedMarker = parseMarkerKey(selectedMarkerKey.current);
        if(lastCoords.current == null || selectedMarker == null){
            return [];
        }

        let routList =  routing(
            getNearNode(lastCoords.current.coords),
            getNearNode({
                latitude: selectedMarker.latitude,
                longitude: selectedMarker.longitude
            })
        );
        let ret = [];
        for (let i = 0; i < routList.length; i++) {
            ret[i] = [routList[i].latitude, routList[i].longitude];
        }
        return ret;
    },[selectedHistory , lastCoords.current]);

    const getActiveHistory = () => {
        let elements = [];
        if(isLoading){
          return elements;
        }
        for (let index = 0; index < historyList.length; index++) {
            let history = historyList[index];
            if(sharedLang === LANGS.ENGLISH){
                history.name = history.nameEnglish;
                history.subtitle = history.subtitleEnglish;
                history.description = history.descriptionEnglish;
                history.sound = ''; //英語版は準備していない
            }
            elements.push(history);
        }
        return elements;
    }

    if (isLoading ||isRouteLoading) {
        return (<Loading></Loading>);
    }
    activeHistoryList.current = getActiveHistory();


    if(id && defaultSelectedHistory.current == null){
        activeHistoryList.current.forEach((element) =>{
            if(String(element.id) === id){
                defaultSelectedHistory.current = element;
                selectHistory(element);
                selectedMarkerKey.current = getMarkerKey(
                    element,
                    [element.positions[0].latitude , element.positions[0].longitude]
                );
            }
        })
    }

    return (
        <>
        <Header 
          display={true} 
          title={t("history.title")} 
          isList={isList} 
          toggleList={toggleList} />
        <Grid container justifyContent="center" alignItems="center" direction="column">
        {isList?
        <MapListHistory
            histories={activeHistoryList.current}
            selectHistory={selectHistory}
        />  :
        <>
        <Map ref={mapRef}>
        {
            activeHistoryList.current.map((element, fIndex) =>
                element.positions.map((position, pIndex) => {
                    let coordinate = [position.latitude, position.longitude];
                    let markerKey = getMarkerKey(element, coordinate);

                    return <Marker 
                        key={markerKey} 
                        position={coordinate}
                        icon={getIcon(element.markerIcon)}
                        eventHandlers={{
                            click: (e) => {
                              clickMarker(element, coordinate);
                            }
                          }}
                        >
                        {
                        isActiveMarker(element)
                        //true
                        ?
                        <Tooltip
                            {...getTooltipOptions(
                                fIndex + pIndex , 
                                coordinate,
                                element.category
                            )}
                        >
                            {element.name}
                        </Tooltip>
                        : <></>
                        }
                    </Marker>
                })
            )
        }
        <div>{isCanGPS && inRouteGuid && <Polyline positions={getPolyline()} />}</div>
        </Map>
        <MapCardHistory 
            history={getActiveElement()} 
            resetMarker={resetMarker} 
            isRouteEnabled={isCanGPS}
            inRouteGuid = {inRouteGuid}
            toggleRouteGuid = {toggleRouteGuid}
            modalOpen = {modalOpen}
            />
        </>
        }      
        </Grid>
        </>
    )
});
export default History